/**
 * Table2 Utility Functions
 * Pure calculation, math, formatting, and pagination helpers
 */

export type PageItem = number | "ellipsis-l" | "ellipsis-r";

/**
 * Extracts 2-letter uppercase initials from a full name
 */
export function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Returns color hex for utilization percentage based on standard thresholds
 * ≥100% -> Emerald (#059669)
 * ≥85%  -> Blue (#2563EB)
 * <85%  -> Amber (#B45309)
 */
export function getUtilColor(pct: number): string {
  if (pct >= 100) return "#059669";
  if (pct >= 85) return "#2563EB";
  return "#B45309";
}

/**
 * Calculates utilization percentage, bar width, and corresponding color
 */
export function calculateUtilization(
  totalHours: number,
  fullDayHours: number
): { pct: number; width: number; color: string } {
  const pct =
    fullDayHours > 0
      ? Math.round(((totalHours || 0) / fullDayHours) * 100)
      : 0;
  const width = Math.min(100, Math.max(0, pct));
  const color = getUtilColor(pct);
  return { pct, width, color };
}

/**
 * Calculates total pages, start item, and end item indices for a paginated list
 */
export function calculatePaginationRange(
  total: number,
  page: number,
  pageSize: number
): {
  totalPages: number;
  startItem: number;
  endItem: number;
} {
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(total / safePageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));
  const startItem = total === 0 ? 0 : (safePage - 1) * safePageSize + 1;
  const endItem = Math.min(safePage * safePageSize, total);

  return { totalPages, startItem, endItem };
}

/**
 * Generates page number sequence with ellipsis markers for pagination controls
 */
export function getPageItems(totalPages: number, current: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items: PageItem[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);

  if (start > 2) items.push("ellipsis-l");
  for (let i = start; i <= end; i++) {
    items.push(i);
  }
  if (end < totalPages - 1) items.push("ellipsis-r");

  items.push(totalPages);
  return items;
}
